/**
 * Pipeline chip switcher container (SE-5489, desktop).
 *
 * Orchestrates the recruitment-pipeline chip row + the all-pipelines drawer,
 * mirroring the Scouting `WatchlistPillsRow` + `SeeAllListsDrawer` pattern.
 *
 * Chip model (SE-6857 — "show the last 3 opened pipelines, like Watchlist"):
 * the row is a LIVE, recency-ordered, hard-capped-at-3 list — the same shape
 * `WatchlistPillsRowContainer` derives from `lastSelectedAt DESC`. The order is
 * `activeBoardId` first, then the recency list, deduped, resolved against the
 * user's PIPELINE boards, and sliced to `RECENTS_CAP`.
 *
 * SE-6857 replaces two mechanisms that SE-5489/SE-6538 had layered on top of
 * that list and that QA reported as wrong:
 *  - a ONE-TIME SNAPSHOT of the recency list, which froze the visible order for
 *    the life of the row, so a pipeline opened later never rose to the front;
 *  - an APPEND-ONLY visible order, which grew past 3 (the cap applied only to
 *    the recents half) and pinned each chip to the slot it first appeared in.
 * Both are gone. "Most recent first, at most 3" is now the whole rule, which is
 * the behaviour the ticket asks to match. The visible cost is deliberate and
 * was the reason SE-5489 froze the order in the first place: opening a pipeline
 * that is already on screen moves its chip to position 1, so the row reorders
 * under the cursor. SE-6857 accepts that in exchange for chips that actually
 * report the last 3 opened pipelines.
 *
 * Active pipeline vs. the cap: `activeBoardId` leads the order, so the pipeline
 * the user is looking at is ALWAYS chip 1 and can never be pushed out by the
 * cap. Opening a 4th pipeline therefore drops the least-recently-opened chip,
 * never the one on screen. Leading with the active id also means the row shows
 * its post-bump order on the very render the board changes, instead of
 * flickering through an intermediate order while the recency effect lands.
 *
 * Persistence scope: pipeline recency is client-local (AsyncStorage via
 * `pipelineRecencyStore`). `KanbanBoard` has no `lastSelectedAt` and the schema
 * has no mark-recently-selected mutation, so unlike Watchlist this does NOT
 * sync across devices. That is a DECISION for SE-6857 (behaviour first, no
 * backend field requested), not an oversight.
 * @module features/player-kanban/components/molecules/PipelineChipsRow/PipelineChipsRowContainer
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import PipelineChipsRowView, {
  type PipelineChipItem,
} from "./PipelineChipsRowView";

import SeeAllPipelinesDrawer from "@/features/player-kanban/components/molecules/SeeAllPipelinesDrawer";
import { usePipelineRecency } from "@/features/player-kanban/hooks/usePipelineRecency";
import { usePipelineSwitcherBoards } from "@/features/player-kanban/hooks/usePipelineSwitcherBoards";
import { bumpRecentPipeline } from "@/features/player-kanban/stores/pipelineRecencyStore";

import { KanbanSourceType } from "@/generated/graphql";

/** Cap on recent chips — mirrors the watchlist pills row (3 recents). */
const RECENTS_CAP = 3;

/** Props for `PipelineChipsRowContainer`. */
export interface PipelineChipsRowContainerProps {
  /** Currently active pipeline board id (null when no pipeline is active). */
  readonly activeBoardId: string | null;
  /** Selection callback — switches the active board. Fired by chip taps and drawer picks. */
  readonly onSelectPipeline: (boardId: string) => void;
  /**
   * SE-6537: creation callback — opens the "+ New pipeline" flow. Passed
   * straight through to the view; this container deliberately owns no creation
   * state, because the modal's open/creating state already belongs to
   * `usePipelineCreationFlow` at the screen root. Optional so a DIRECT
   * consumer that has not been migrated renders no trigger rather than a dead
   * one — the real `TopPanel` path always supplies it (see the note on
   * `PipelineChipsRowViewProps.onCreatePipeline`).
   */
  readonly onCreatePipeline?: () => void;
}

/**
 * Renders the pipeline chip switcher + all-pipelines drawer.
 * @param root0 - Component props
 * @param root0.activeBoardId - Active pipeline board id (null = none active)
 * @param root0.onSelectPipeline - Board selection callback
 * @param root0.onCreatePipeline - Pipeline-creation callback (SE-6537)
 * @returns The chip row with its drawer mounted.
 */
const PipelineChipsRowContainer = ({
  activeBoardId,
  onSelectPipeline,
  onCreatePipeline,
}: PipelineChipsRowContainerProps) => {
  const recentIds = usePipelineRecency();

  // SE-6597: `hasUnreachableBoards` is the switcher walk reporting that it
  // stopped while the connection still had pages. It used to be a bare
  // `console.warn`, so the row rendered a short board set that a user could not
  // tell apart from a complete one. Passing it through is what turns that into
  // something on screen.
  const { edges, hasUnreachableBoards } = usePipelineSwitcherBoards({
    fetchPolicy: "cache-and-network",
  });

  /** PIPELINE boards keyed by id, for recency/active-id resolution. */
  const boardsById = useMemo((): Readonly<Record<string, PipelineChipItem>> => {
    return edges.reduce(
      (acc, edge) => {
        const node = edge.node;
        if (node.sourceType !== KanbanSourceType.Pipeline) return acc;
        return { ...acc, [node.id]: { id: node.id, name: node.name } };
      },
      {} as Record<string, PipelineChipItem>
    );
  }, [edges]);

  /**
   * SE-6857: the visible chips — the last 3 opened pipelines, most recent
   * first. Active board leads (see the module note on the cap), then the
   * recency list; duplicates drop, ids that no longer resolve to a PIPELINE
   * board (deleted elsewhere, or a watchlist/roster board id) drop, and the
   * result is capped. Resolving THROUGH `boardsById` is what keeps the cap
   * honest: a stale id consumes no slot, so a user with 3 live pipelines always
   * sees 3 chips.
   */
  const chips = useMemo((): readonly PipelineChipItem[] => {
    const orderedIds = [
      ...(activeBoardId ? [activeBoardId] : []),
      ...recentIds,
    ];
    return orderedIds
      .filter((id, index) => orderedIds.indexOf(id) === index)
      .map(id => boardsById[id])
      .filter((chip): chip is PipelineChipItem => Boolean(chip))
      .slice(0, RECENTS_CAP);
  }, [activeBoardId, recentIds, boardsById]);

  /**
   * Whether the active board resolves to a PIPELINE board we know about. Gating
   * the bump below on this keeps ids that are stale, deleted, or simply not yet
   * loaded out of the recency list.
   */
  const isActiveBoardResolved = Boolean(
    activeBoardId && boardsById[activeBoardId]
  );

  /**
   * Stable per-chip tap handlers. SE-6857: a chip tap bumps recency like any
   * other open path — the ticket's "opening a pipeline (from the drawer, a deep
   * link, or a chip) updates the recency order". The bump is explicit here
   * rather than left to the active-board effect below so the recency list is
   * correct the moment the user taps, even if the parent declines the
   * selection.
   */
  const chipHandlers = useMemo(
    (): Readonly<Record<string, () => void>> =>
      chips.reduce(
        (acc, chip) => ({
          ...acc,
          [chip.id]: () => {
            onSelectPipeline(chip.id);
            bumpRecentPipeline(chip.id);
          },
        }),
        {} as Record<string, () => void>
      ),
    [chips, onSelectPipeline]
  );

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const handleOpenDrawer = useCallback(() => setIsDrawerOpen(true), []);
  const handleCloseDrawer = useCallback(() => setIsDrawerOpen(false), []);

  /**
   * Drawer pick: switch the board AND bump recency, so the picked pipeline
   * leads the row immediately rather than waiting for the next mount.
   */
  const handleDrawerSelect = useCallback(
    (boardId: string) => {
      onSelectPipeline(boardId);
      bumpRecentPipeline(boardId);
    },
    [onSelectPipeline]
  );

  // SE-6857: EVERY path that opens a pipeline registers it as most-recent, not
  // just a drawer pick. `activeBoardId` is the one signal all of those paths
  // converge on: a chip tap and a drawer pick route through `onSelectPipeline`
  // -> MainContainer's `switchBoard`; a `?boardId=` deep link routes through
  // `useBoardDeepLink` -> the same `switchBoard`; so do a board restored on
  // mount and one just created by the "+ New pipeline" flow. Observing it here
  // covers all of them without editing MainContainer or the source-type-
  // agnostic `useBoardDeepLink` — and because this container only mounts on the
  // PIPELINE surface, no watchlist/roster board id can reach the pipeline
  // store. Not a setState-in-effect: the store owns its reactive var, and
  // `bumpRecentPipeline` no-ops when the id is already the most recent, so the
  // re-render it triggers cannot loop.
  //
  // Placed after the definitions above to satisfy
  // `code-organization/enforce-statement-order` — definitions, then side
  // effects, then the return.
  useEffect(() => {
    if (!activeBoardId || !isActiveBoardResolved) return;
    bumpRecentPipeline(activeBoardId);
  }, [activeBoardId, isActiveBoardResolved]);

  return (
    <>
      <PipelineChipsRowView
        chips={chips}
        selectedBoardId={activeBoardId}
        isEmpty={chips.length === 0}
        chipHandlers={chipHandlers}
        onOpenSeeAllDrawer={handleOpenDrawer}
        onCreatePipeline={onCreatePipeline}
        hasUnreachableBoards={hasUnreachableBoards}
      />
      <SeeAllPipelinesDrawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        onSelectPipeline={handleDrawerSelect}
        onCreatePipeline={onCreatePipeline}
      />
    </>
  );
};

PipelineChipsRowContainer.displayName = "PipelineChipsRowContainer";

export default PipelineChipsRowContainer;
